import React, { useEffect, useContext } from 'react';
import styled from 'styled-components/macro';

import { PromiseState } from 'react-refetch';

import getPlaylistKPIURL from 'url-constructors/getPlaylistKPIURL';
import getPlaylistTableURL from 'url-constructors/getPlaylistTableURL';

import connect from 'helpers/apiConnect';
import { EntityContext } from 'context/entityContext';

import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';

import OverviewContainer from 'components/Overview/Container/Container';
import PlaylistKPIs from './PlaylistKPIs';
import PlaylistsTable from './PlaylistsTable';
import { MediaIcon, MediaContent } from 'components/base/Message/Message';
import { ReactComponent as NotifyIcon } from 'icons/icon-notify.svg';

const MessageBlock = styled.article`
  justify-content: flex-start;
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  background: white;
  position: relative;
  padding-left: 0;
  padding-top: 0;
  margin-bottom: ${({ theme }): string => theme.spacing.medium};
  margin-top: 0;
`;

function dataSources(): any {
  return {
    fetchKPIData: (args: any): any => ({
      kpiData: getPlaylistKPIURL(args),
    }),
    fetchPlaylists: (args: any): any => ({
      playlistData: getPlaylistTableURL(args),
    }),
  };
}

interface IPlaylistPerformanceProps {
  fetchKPIData: Function;
  fetchPlaylists: Function;
  kpiData: PromiseState<any>;
  playlistData: PromiseState<any>;
  showPlaylistData: boolean;
}

/*
 * The playlist performance card shows KPIs and a breakdown table.
 *
 * ## Differences per entity type
 *
 * ### Artist
 * KPIs showing playlist count, streams, and skip rate.
 *
 * ### Track, release, project
 * KPIs and a table with the top 5 playlists.
 *
 * ## Differences per metric (from the metric dropdown)
 * Last full week data is not supported and shows a message instead.
 */
const PlaylistPerformance: React.FC<IPlaylistPerformanceProps> = (props) => {
  const {
    fetchKPIData,
    fetchPlaylists,
    showPlaylistData = true,
    kpiData = PromiseState.create(),
    playlistData = PromiseState.create(),
  } = props;

  const activeMetric = 'stream_count_this_week_to_date';
  const ctx = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    compareMode,
    countryIds,
    filters,
    viewSettings,
    customRegionFilter,
  } = ctx;
  const timeframe = viewSettings?.timeframe ?? 'twtd';
  const showCardMessage = timeframe !== 'twtd';

  useEffect(() => {
    const args = {
      entityIds,
      entityType,
      countryIds:
        compareMode === 'country' ? countryIds : filters?.country ?? undefined,
      region: filters?.region,
      customRegion: customRegionFilter,
      compareMode,
    };

    fetchKPIData(args);

    if (showPlaylistData) {
      fetchPlaylists({
        ...args,
        limit: 5,
        sortby: activeMetric,
      });
    }
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    fetchKPIData,
    fetchPlaylists,
    filters,
    showPlaylistData,
    customRegionFilter,
  ]);

  const promiseStates = showPlaylistData ? [kpiData, playlistData] : [kpiData];
  const [, pending] = useCombinedRefetchState(promiseStates);

  return (
    <OverviewContainer
      filtersToShow={['region', 'country', 'customRegion']}
      title="Playlists"
      linkTo={useUrlForTopic('performance/playlist')}
    >
      {showCardMessage && (
        <MessageBlock data-pw="MessageBlock">
          <MediaIcon>
            <NotifyIcon />
          </MediaIcon>
          <MediaContent>
            {' '}
            Playlist data is only available and shown for the current week in
            progress.
          </MediaContent>
        </MessageBlock>
      )}
      {<PlaylistKPIs kpiData={kpiData} isPending={pending} />}
      {showPlaylistData && (
        <PlaylistsTable
          playlistData={playlistData}
          isPending={pending}
          headers={[
            'playlist',
            'position',
            'streams',
            'peak',
            'streams week over week',
            'skiprate',
            'followers',
            'track count',
          ]}
        />
      )}
    </OverviewContainer>
  );
};

export default connect(dataSources as any)(PlaylistPerformance as any) as any;
